import { ChangeEvent, FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Input } from 'src/components/common/Input';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { useResetSignUpError } from 'src/hooks/useResetSignupErrors';
import { setError } from 'src/store/user/actions';
import { RootState } from 'src/types/store-types';
import * as Yup from 'yup';

type IProps = {
  name: string;
  value: string;
  regexp: string;
  onChange: (e: any) => void;
  placeholder?: string;
  required?: boolean;
};
const TaxId_Input: FC<IProps> = (props) => {
  // Hooks
  const { t }: Translation = useTranslation();

  // Redux
  const _setErrorReq = useConnectedAction(setError);
  const resetSignUpError = useResetSignUpError();

  // Selectors
  const { errors: signUpErrors, dniResult } = useSelector((state: RootState) => state.user);
  const { cuit } = dniResult || {};

  // YUP Validation
  const validationSchema = Yup.object().shape({
    taxId: Yup.string().required(t('requiredField')).matches(new RegExp(props.regexp), t('zipfieldLength')),
  });

  const handleValidate = async (value: string): Promise<void> => {
    try {
      await validationSchema.validate({ taxId: value }, { abortEarly: false });
      resetSignUpError('taxId');
    } catch (error: any) {
      if (error.name === 'ValidationError') {
        _setErrorReq({ ...signUpErrors, taxId: error.errors[0] });
      }
    }
  };

  // Actions
  const handleOnChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (signUpErrors.taxId) {
      resetSignUpError('taxId');
    }
    props.onChange(e);
  };

  const handleBlur = (): void => {
    handleValidate(props.value?.trim());
  };

  useEffect(() => {
    if (Object.keys(dniResult).length) {
      props.onChange({
        target: {
          name: props.name,
          value: cuit,
        },
      });
      handleValidate(cuit);
    }
  }, [dniResult]);

  return (
    <Input
      error={signUpErrors.taxId}
      {...props}
      onChange={handleOnChange}
      onBlur={handleBlur}
      type="number"
      disableNumberSymbols
    />
  );
};
export default TaxId_Input;
